import { Controller } from '@hotwired/stimulus';
import { Turbo } from "@hotwired/turbo-rails";
import hotkeys from 'hotkeys-js';
import Toastify from 'toastify-js';
import { log } from "../../javascript/utils/logger";

let toastCounter = 0;

const successToast = (text) => {
  const id = `toast-success-${++toastCounter}`;
  return `
  <div class="max-w-xs bg-teal-100 border border-teal-200 text-sm text-teal-800 rounded-lg dark:bg-teal-800/10 dark:border-teal-900 dark:text-teal-500" role="alert" aria-live="polite" aria-atomic="true" tabindex="-1" aria-labelledby="${id}">
    <div id="${id}" class="flex p-4">
      ${text}

      <div class="ms-auto">
        <button type="button" qaid="toast-close" class="inline-flex shrink-0 justify-center items-center size-5 rounded-lg text-teal-800 opacity-50 hover:opacity-100 focus:outline-hidden focus:opacity-100 dark:text-teal-200" aria-label="Close">
          <span class="sr-only">Close</span>
          <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
`;
};

const dangerToast = (text) => {
  const id = `toast-danger-${++toastCounter}`;
  return `
  <div class="bg-red-100 border border-red-200 text-sm text-red-800 rounded-lg dark:bg-red-800/10 dark:border-red-900 dark:text-red-500" role="alert" aria-live="assertive" aria-atomic="true" tabindex="-1" aria-labelledby="${id}">
    <div id="${id}" class="flex p-4">
      ${text}

      <div class="ms-auto">
        <button type="button" qaid="toast-close" class="inline-flex shrink-0 justify-center items-center size-5 rounded-lg text-red-800 opacity-50 hover:opacity-100 focus:outline-hidden focus:opacity-100 dark:text-red-200" aria-label="Close">
          <span class="sr-only">Close</span>
          <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
`;
};

const warningToast = (text) => {
  const id = `toast-warning-${++toastCounter}`;
  return `
  <div class="max-w-xs bg-yellow-100 border border-yellow-200 text-sm text-yellow-800 rounded-lg dark:bg-yellow-800/10 dark:border-yellow-900 dark:text-yellow-500" role="alert" aria-live="assertive" aria-atomic="true" tabindex="-1" aria-labelledby="${id}">
    <div id="${id}" class="flex p-4">
      ${text}

      <div class="ms-auto">
        <button type="button" qaid="toast-close" class="inline-flex shrink-0 justify-center items-center size-5 rounded-lg text-yellow-800 opacity-50 hover:opacity-100 focus:outline-hidden focus:opacity-100 dark:text-yellow-200" aria-label="Close">
          <span class="sr-only">Close</span>
          <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
`;
};

const infoToast = (text) => {
  const id = `toast-info-${++toastCounter}`;
  return `
  <div class="max-w-xs bg-gray-100 border border-gray-200 text-sm text-gray-800 rounded-lg dark:bg-white/10 dark:border-white/20 dark:text-white" role="alert" aria-live="polite" aria-atomic="true" tabindex="-1" aria-labelledby="${id}">
    <div id="${id}" class="flex p-4">
      ${text}

      <div class="ms-auto">
        <button type="button" qaid="toast-close" class="inline-flex shrink-0 justify-center items-center size-5 rounded-lg text-gray-800 opacity-50 hover:opacity-100 focus:outline-hidden focus:opacity-100 dark:text-white" aria-label="Close">
          <span class="sr-only">Close</span>
          <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
`;
};

export default class extends Controller {
  static values = {
    text: String,
    duration: { type: Number, default: 3000 },
    type: { type: String, default: "info" },
    actionUrl: { type: String, default: "" },
    actionLabel: { type: String, default: "" },
    actionMethod: { type: String, default: "" },
    container: { type: String, default: "" } // element id to render into, instead of <body>
  }

  connect() {
    log("toast connect", this.textValue, this.typeValue, this.durationValue)

    // A permanent container is re-attached on every Turbo visit, which
    // reconnects this controller: keep the toast already showing.
    if (this.closeToast) {
      hotkeys('esc', this.closeToast)
      return
    }

    const displayText = this.actionUrlValue
      ? `${this.textValue} <a href="${this.actionUrlValue}" class="underline font-medium ms-2">${this.actionLabelValue}</a>`
      : this.textValue
    const markupElement = this.renderMarkup(displayText, this.typeValue)
    const toast = Toastify({
      node: markupElement,
      duration: this.durationValue,
      selector: this.containerValue || undefined,
      gravity: "top",
      position: "left",
      className: "hs-toastify-on:opacity-100 opacity-0 fixed -top-[150px] right-[20px] z-[90] transition-all duration-300 w-[320px] rounded-xl shadow-lg [&>.toast-close]:hidden bg-white dark:bg-neutral-800",
      close: true,
      stopOnFocus: true,
      callback: () => {
        log("toast closed", this.textValue)
        this.element.remove()
      }
    }).showToast()

    this.closeToast = () => {
      toast.toastElement.querySelector('.toast-close').click()
    }

    // Close the toast when the close button is clicked
    const closeButton = markupElement.querySelector('button')
    closeButton.addEventListener("click", this.closeToast)

    // Toastify stops click propagation on every toast (its default onClick), so
    // Turbo never sees the action link: follow it here instead.
    markupElement.querySelector("a")?.addEventListener("click", (event) => this.followAction(event))

    // Close the toast when the escape key is pressed
    hotkeys('esc', this.closeToast)
  }

  followAction(event) {
    event.preventDefault()
    const method = this.actionMethodValue.toLowerCase()
    if (!method || method === "get") return Turbo.visit(this.actionUrlValue)

    const form = document.createElement("form")
    form.method = "post"
    form.action = this.actionUrlValue
    form.hidden = true
    for (const [name, value] of [["_method", method], ["authenticity_token", document.querySelector('meta[name="csrf-token"]')?.content || ""]]) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      form.appendChild(input)
    }
    form.addEventListener("turbo:submit-end", () => form.remove())
    document.body.appendChild(form)
    form.requestSubmit()
  }

  disconnect() {
    log("toast disconnect")
    hotkeys.unbind('esc', this.closeToast)
  }

  renderMarkup(text, type) {
    let markup;
    switch (type) {
      case "success":
        markup = successToast(text)
        break;
      case "warning":
        markup = warningToast(text)
        break;
      case "danger":
        markup = dangerToast(text)
        break;
      case "info":
      default:
        markup = infoToast(text)
        break;
    }

    const element = document.createElement("div")
    element.innerHTML = markup
    return element
  }
}
